/**
 * Bilinear lattice warp.
 *
 * A "lattice" is a 2D grid of (rows+1) × (cols+1) control points laid over a
 * source viewBox. Each row j is a horizontal mesh line, each column i a
 * vertical mesh line. Moving a node displaces the underlying SVG by bilinearly
 * blending the four corner targets of whichever source cell a point falls in.
 *
 * The exported `makeBilinearWarp` returns a `DisplaceFn`-compatible function
 * that can be fed straight into the warp pipeline exported from
 * `./displacementMap` (`warpSvgWithFn`).
 */

export type LatticePoint = { x: number; y: number };

/** A `(rows+1) × (cols+1)` array indexed as `lattice[j][i]`. */
export type Lattice = LatticePoint[][];

export type ViewBox = { x: number; y: number; width: number; height: number };

/**
 * Returns the source-space position a node at column `i`, row `j` would have
 * before any user editing — i.e. the identity lattice that warps to itself.
 */
export function identityLatticePoint(
    i: number,
    j: number,
    cols: number,
    rows: number,
    vb: ViewBox,
): LatticePoint {
    return {
        x: vb.x + (i / cols) * vb.width,
        y: vb.y + (j / rows) * vb.height,
    };
}

/** Build an identity lattice (every node sits on its source grid intersection). */
export function makeIdentityLattice(
    cols: number,
    rows: number,
    vb: ViewBox,
): Lattice {
    const out: Lattice = [];
    for (let j = 0; j <= rows; j++) {
        const row: LatticePoint[] = [];
        for (let i = 0; i <= cols; i++) {
            row.push(identityLatticePoint(i, j, cols, rows, vb));
        }
        out.push(row);
    }
    return out;
}

/**
 * Resample an existing lattice to a new resolution, preserving shape: sample
 * the previous lattice at the new node's normalized `(u, v)` using bilinear
 * interpolation. Used when the user changes cols/rows from the controls.
 */
export function resizeLattice(
    prev: Lattice,
    prevCols: number,
    prevRows: number,
    cols: number,
    rows: number,
): Lattice {
    if (!prev || prev.length === 0) {
        // Caller should have a viewBox to fall back on; we mirror identity at
        // unit scale. Realistically `makeIdentityLattice` is what they want.
        return [];
    }
    const out: Lattice = [];
    for (let j = 0; j <= rows; j++) {
        const v = rows === 0 ? 0 : j / rows;
        const fy = v * prevRows;
        const j0 = Math.floor(fy);
        const j1 = Math.min(j0 + 1, prevRows);
        const ty = fy - j0;
        const row: LatticePoint[] = [];
        for (let i = 0; i <= cols; i++) {
            const u = cols === 0 ? 0 : i / cols;
            const fx = u * prevCols;
            const i0 = Math.floor(fx);
            const i1 = Math.min(i0 + 1, prevCols);
            const tx = fx - i0;

            const p00 = prev[j0][i0];
            const p10 = prev[j0][i1];
            const p01 = prev[j1][i0];
            const p11 = prev[j1][i1];

            const a = p00.x * (1 - tx) + p10.x * tx;
            const b = p01.x * (1 - tx) + p11.x * tx;
            const x = a * (1 - ty) + b * ty;

            const c = p00.y * (1 - tx) + p10.y * tx;
            const d = p01.y * (1 - tx) + p11.y * tx;
            const y = c * (1 - ty) + d * ty;

            row.push({ x, y });
        }
        out.push(row);
    }
    return out;
}

/**
 * Build a `DisplaceFn` from a lattice. For a source point `(x, y)`:
 * 1. Compute its normalized `(u, v)` in source space relative to `vb`.
 * 2. Find the source cell `(i, j)` (clamped to `[0, cols-1] × [0, rows-1]`).
 * 3. Bilinearly blend the four target corners.
 *
 * Points outside the source viewBox are clamped to the nearest cell and
 * extrapolated by the bilinear formula naturally.
 */
export function makeBilinearWarp(
    lattice: Lattice,
    vb: ViewBox,
    cols: number,
    rows: number,
): (x: number, y: number) => { x: number; y: number } {
    const W = vb.width || 1;
    const H = vb.height || 1;

    return (x, y) => {
        const u = ((x - vb.x) / W) * cols;
        const v = ((y - vb.y) / H) * rows;

        let i = Math.floor(u);
        let j = Math.floor(v);
        if (i < 0) i = 0;
        else if (i > cols - 1) i = cols - 1;
        if (j < 0) j = 0;
        else if (j > rows - 1) j = rows - 1;

        const tx = u - i;
        const ty = v - j;

        const p00 = lattice[j][i];
        const p10 = lattice[j][i + 1];
        const p01 = lattice[j + 1][i];
        const p11 = lattice[j + 1][i + 1];

        const ax = p00.x * (1 - tx) + p10.x * tx;
        const bx = p01.x * (1 - tx) + p11.x * tx;
        const ox = ax * (1 - ty) + bx * ty;

        const ay = p00.y * (1 - tx) + p10.y * tx;
        const by = p01.y * (1 - tx) + p11.y * tx;
        const oy = ay * (1 - ty) + by * ty;

        return { x: ox, y: oy };
    };
}

// ─── Path resampling ─────────────────────────────────────────────────────────

function fmt(n: number): string {
    return parseFloat(n.toFixed(3)).toString();
}

function dist(ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax;
    const dy = by - ay;
    return Math.sqrt(dx * dx + dy * dy);
}

/** Max De Casteljau recursion depth before we fall back to uniform sampling. */
const MAX_BEZIER_RECURSE_DEPTH = 40;
/** Upper bound on uniform samples per curve when falling back (keeps output bounded). */
const MAX_UNIFORM_STEPS = 512;

/** Subdivide a straight segment into `L` pieces of length ≤ maxSegLen. */
function subdivideLine(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    maxSegLen: number,
    out: string[],
): void {
    const len = dist(ax, ay, bx, by);
    const steps = Math.max(1, Math.ceil(len / maxSegLen));
    for (let k = 1; k <= steps; k++) {
        const t = k / steps;
        out.push(`L${fmt(ax + (bx - ax) * t)},${fmt(ay + (by - ay) * t)}`);
    }
}

function evalCubic(
    t: number,
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    p3x: number, p3y: number,
): { x: number; y: number } {
    const u = 1 - t;
    const u2 = u * u;
    const u3 = u2 * u;
    const t2 = t * t;
    const t3 = t2 * t;
    return {
        x: u3 * p0x + 3 * u2 * t * p1x + 3 * u * t2 * p2x + t3 * p3x,
        y: u3 * p0y + 3 * u2 * t * p1y + 3 * u * t2 * p2y + t3 * p3y,
    };
}

/** Last-resort: sample the cubic at uniform t so recursion cannot blow the stack. */
function sampleCubicUniform(
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    p3x: number, p3y: number,
    maxSegLen: number,
    out: string[],
): void {
    const net =
        dist(p0x, p0y, p1x, p1y) +
        dist(p1x, p1y, p2x, p2y) +
        dist(p2x, p2y, p3x, p3y);
    const chord = dist(p0x, p0y, p3x, p3y);
    const est = Math.max(net, chord, maxSegLen);
    let steps = Math.ceil(est / maxSegLen);
    steps = Math.min(MAX_UNIFORM_STEPS, Math.max(2, steps));
    for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const p = evalCubic(t, p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y);
        out.push(`L${fmt(p.x)},${fmt(p.y)}`);
    }
}

function evalQuad(
    t: number,
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
): { x: number; y: number } {
    const u = 1 - t;
    return {
        x: u * u * p0x + 2 * u * t * p1x + t * t * p2x,
        y: u * u * p0y + 2 * u * t * p1y + t * t * p2y,
    };
}

function sampleQuadUniform(
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    maxSegLen: number,
    out: string[],
): void {
    const net = dist(p0x, p0y, p1x, p1y) + dist(p1x, p1y, p2x, p2y);
    const chord = dist(p0x, p0y, p2x, p2y);
    const est = Math.max(net, chord, maxSegLen);
    let steps = Math.ceil(est / maxSegLen);
    steps = Math.min(MAX_UNIFORM_STEPS, Math.max(2, steps));
    for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const p = evalQuad(t, p0x, p0y, p1x, p1y, p2x, p2y);
        out.push(`L${fmt(p.x)},${fmt(p.y)}`);
    }
}

/** De Casteljau subdivision of a cubic Bezier; emits dense `L` segments. */
function subdivideCubic(
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    p3x: number, p3y: number,
    maxSegLen: number,
    out: string[],
    depth = 0,
): void {
    if (depth >= MAX_BEZIER_RECURSE_DEPTH) {
        sampleCubicUniform(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y, maxSegLen, out);
        return;
    }

    // Chord vs control-net heuristic: if the curve is "flat enough" (chord
    // length close to control-net length AND chord ≤ maxSegLen), emit a line.
    const chord = dist(p0x, p0y, p3x, p3y);
    const net =
        dist(p0x, p0y, p1x, p1y) +
        dist(p1x, p1y, p2x, p2y) +
        dist(p2x, p2y, p3x, p3y);

    const flatTol = Math.max(0.25 * maxSegLen, 1e-9 * Math.max(1, net));
    if (chord <= maxSegLen && net - chord < flatTol) {
        out.push(`L${fmt(p3x)},${fmt(p3y)}`);
        return;
    }

    const m01x = (p0x + p1x) / 2, m01y = (p0y + p1y) / 2;
    const m12x = (p1x + p2x) / 2, m12y = (p1y + p2y) / 2;
    const m23x = (p2x + p3x) / 2, m23y = (p2y + p3y) / 2;
    const m012x = (m01x + m12x) / 2, m012y = (m01y + m12y) / 2;
    const m123x = (m12x + m23x) / 2, m123y = (m12y + m23y) / 2;
    const mx = (m012x + m123x) / 2, my = (m012y + m123y) / 2;

    subdivideCubic(p0x, p0y, m01x, m01y, m012x, m012y, mx, my, maxSegLen, out, depth + 1);
    subdivideCubic(mx, my, m123x, m123y, m23x, m23y, p3x, p3y, maxSegLen, out, depth + 1);
}

function subdivideQuad(
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    maxSegLen: number,
    out: string[],
    depth = 0,
): void {
    if (depth >= MAX_BEZIER_RECURSE_DEPTH) {
        sampleQuadUniform(p0x, p0y, p1x, p1y, p2x, p2y, maxSegLen, out);
        return;
    }
    const chord = dist(p0x, p0y, p2x, p2y);
    const net = dist(p0x, p0y, p1x, p1y) + dist(p1x, p1y, p2x, p2y);
    const flatTol = Math.max(0.25 * maxSegLen, 1e-9 * Math.max(1, net));
    if (chord <= maxSegLen && net - chord < flatTol) {
        out.push(`L${fmt(p2x)},${fmt(p2y)}`);
        return;
    }
    const m01x = (p0x + p1x) / 2, m01y = (p0y + p1y) / 2;
    const m12x = (p1x + p2x) / 2, m12y = (p1y + p2y) / 2;
    const mx = (m01x + m12x) / 2, my = (m01y + m12y) / 2;
    subdivideQuad(p0x, p0y, m01x, m01y, mx, my, maxSegLen, out, depth + 1);
    subdivideQuad(mx, my, m12x, m12y, p2x, p2y, maxSegLen, out, depth + 1);
}

function tokenizePathD(d: string): Array<{ cmd: string; args: number[] }> {
    const tokens: Array<{ cmd: string; args: number[] }> = [];
    const parts = d.trim().match(/[MmZzLlHhVvCcSsQqTtAa][^MmZzLlHhVvCcSsQqTtAa]*/g);
    if (!parts) return tokens;
    for (const part of parts) {
        const cmd = part[0];
        const rest = part.slice(1).trim();
        const args = rest === "" ? [] : rest.split(/[\s,]+/).filter(Boolean).map(Number);
        tokens.push({ cmd, args });
    }
    return tokens;
}

/**
 * Rewrite an SVG `d` attribute so every line/curve segment is broken into
 * pieces no longer than `maxSegLen` (in source units). Output uses only
 * `M`/`L`/`Z` plus pass-through `A` commands. This is the prep step that lets
 * a downstream node-based warp curve smoothly through warped cells.
 */
export function resamplePathD(d: string, maxSegLen: number): string {
    if (!Number.isFinite(maxSegLen) || maxSegLen <= 0) return d;
    const tokens = tokenizePathD(d);
    const out: string[] = [];

    let cx = 0, cy = 0;
    let sx = 0, sy = 0;
    let lastCubicCp2x = 0, lastCubicCp2y = 0;
    let lastWasCubic = false;
    let lastQuadCpx = 0, lastQuadCpy = 0;
    let lastWasQuad = false;

    for (const { cmd, args } of tokens) {
        const isRel = cmd === cmd.toLowerCase() && cmd !== "Z" && cmd !== "z";
        const upper = cmd.toUpperCase();

        if (upper !== "C" && upper !== "S") lastWasCubic = false;
        if (upper !== "Q" && upper !== "T") lastWasQuad = false;

        switch (upper) {
            case "M": {
                for (let k = 0; k + 1 < args.length; k += 2) {
                    const ax = isRel ? cx + args[k]     : args[k];
                    const ay = isRel ? cy + args[k + 1] : args[k + 1];
                    if (k === 0) {
                        out.push(`M${fmt(ax)},${fmt(ay)}`);
                        sx = ax; sy = ay;
                    } else {
                        subdivideLine(cx, cy, ax, ay, maxSegLen, out);
                    }
                    cx = ax; cy = ay;
                }
                break;
            }
            case "Z": {
                if (cx !== sx || cy !== sy) {
                    subdivideLine(cx, cy, sx, sy, maxSegLen, out);
                }
                out.push("Z");
                cx = sx; cy = sy;
                break;
            }
            case "L": {
                for (let k = 0; k + 1 < args.length; k += 2) {
                    const ax = isRel ? cx + args[k]     : args[k];
                    const ay = isRel ? cy + args[k + 1] : args[k + 1];
                    subdivideLine(cx, cy, ax, ay, maxSegLen, out);
                    cx = ax; cy = ay;
                }
                break;
            }
            case "H": {
                for (let k = 0; k < args.length; k++) {
                    const ax = isRel ? cx + args[k] : args[k];
                    subdivideLine(cx, cy, ax, cy, maxSegLen, out);
                    cx = ax;
                }
                break;
            }
            case "V": {
                for (let k = 0; k < args.length; k++) {
                    const ay = isRel ? cy + args[k] : args[k];
                    subdivideLine(cx, cy, cx, ay, maxSegLen, out);
                    cy = ay;
                }
                break;
            }
            case "C": {
                for (let k = 0; k + 5 < args.length; k += 6) {
                    const x1 = isRel ? cx + args[k]     : args[k];
                    const y1 = isRel ? cy + args[k + 1] : args[k + 1];
                    const x2 = isRel ? cx + args[k + 2] : args[k + 2];
                    const y2 = isRel ? cy + args[k + 3] : args[k + 3];
                    const ax = isRel ? cx + args[k + 4] : args[k + 4];
                    const ay = isRel ? cy + args[k + 5] : args[k + 5];
                    subdivideCubic(cx, cy, x1, y1, x2, y2, ax, ay, maxSegLen, out);
                    lastCubicCp2x = x2; lastCubicCp2y = y2; lastWasCubic = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "S": {
                for (let k = 0; k + 3 < args.length; k += 4) {
                    const x2 = isRel ? cx + args[k]     : args[k];
                    const y2 = isRel ? cy + args[k + 1] : args[k + 1];
                    const ax = isRel ? cx + args[k + 2] : args[k + 2];
                    const ay = isRel ? cy + args[k + 3] : args[k + 3];
                    const x1 = lastWasCubic ? 2 * cx - lastCubicCp2x : cx;
                    const y1 = lastWasCubic ? 2 * cy - lastCubicCp2y : cy;
                    subdivideCubic(cx, cy, x1, y1, x2, y2, ax, ay, maxSegLen, out);
                    lastCubicCp2x = x2; lastCubicCp2y = y2; lastWasCubic = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "Q": {
                for (let k = 0; k + 3 < args.length; k += 4) {
                    const x1 = isRel ? cx + args[k]     : args[k];
                    const y1 = isRel ? cy + args[k + 1] : args[k + 1];
                    const ax = isRel ? cx + args[k + 2] : args[k + 2];
                    const ay = isRel ? cy + args[k + 3] : args[k + 3];
                    subdivideQuad(cx, cy, x1, y1, ax, ay, maxSegLen, out);
                    lastQuadCpx = x1; lastQuadCpy = y1; lastWasQuad = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "T": {
                for (let k = 0; k + 1 < args.length; k += 2) {
                    const ax = isRel ? cx + args[k]     : args[k];
                    const ay = isRel ? cy + args[k + 1] : args[k + 1];
                    const x1 = lastWasQuad ? 2 * cx - lastQuadCpx : cx;
                    const y1 = lastWasQuad ? 2 * cy - lastQuadCpy : cy;
                    subdivideQuad(cx, cy, x1, y1, ax, ay, maxSegLen, out);
                    lastQuadCpx = x1; lastQuadCpy = y1; lastWasQuad = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "A": {
                // Pass-through. Elliptical arc subdivision is non-trivial and
                // arcs are rare in glyph/path data; the downstream warp will
                // still move endpoints. Future work could convert A → cubics.
                for (let k = 0; k + 6 < args.length; k += 7) {
                    const rx  = args[k];
                    const ry  = args[k + 1];
                    const rot = args[k + 2];
                    const la  = args[k + 3];
                    const sw  = args[k + 4];
                    const ax  = isRel ? cx + args[k + 5] : args[k + 5];
                    const ay  = isRel ? cy + args[k + 6] : args[k + 6];
                    out.push(`A${fmt(rx)},${fmt(ry)} ${fmt(rot)} ${la},${sw} ${fmt(ax)},${fmt(ay)}`);
                    cx = ax; cy = ay;
                }
                break;
            }
        }
    }

    return out.join(" ");
}

/**
 * Walk an SVG document string and rewrite each `<path d="…" />`'s `d` through
 * `resamplePathD`. Other shape tags are left untouched (the warp pipeline
 * converts rects/circles/ellipses to dense path approximations on its own
 * using `circleSegments`).
 */
export function resamplePathsInSvg(svgContent: string, maxSegLen: number): string {
    if (!Number.isFinite(maxSegLen) || maxSegLen <= 0) return svgContent;
    return svgContent.replace(
        /<path\b([^>]*?)\bd=(["'])([^"']*)\2([^>]*?)(\/?)>/gi,
        (full, pre: string, _q: string, d: string, post: string, slash: string) => {
            const resampled = resamplePathD(d, maxSegLen);
            return `<path${pre}d="${resampled}"${post}${slash}>`;
        },
    );
}
